import React, { useEffect, useState } from "react"
import { Select } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { CustomTimeDropdown } from "../CustomTimeDropdown"

const { Option } = Select

const predefinedOptions = [
  { label: "1 hr", value: 60 },
  { label: "2 hr", value: 120 },
  { label: "3 hr", value: 180 },
  { label: "4 hr", value: 240 }
]

export const ExperienceDuration = ({ form, expandedRowData, value, onChange }) => {
  const [customHour, setCustomHour] = useState(0)
  const [customMinute, setCustomMinute] = useState(0)
  const [error, setError] = useState("")
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const availableHours = form.getFieldValue("available_hours")

  // Durations already used by other booking options — exclude the current row's own duration
  const currentDuration = expandedRowData?.trip_duration
  const takenDurations = new Set(
    (form.getFieldValue("takenDurations") || []).filter((d) => d !== currentDuration)
  )

  // Calculate total available minutes from availableHours
  const getTotalAvailableMinutes = (availableHours) => {
    if (!Array.isArray(availableHours) || availableHours.length === 0) return 0
    return availableHours.reduce((total, b) => {
      if (!b?.start || !b?.end) return total
      const start = typeof b.start === "string" && window.dayjs ? window.dayjs(b.start) : b.start
      const end = typeof b.end === "string" && window.dayjs ? window.dayjs(b.end) : b.end
      if (!start || !end || !start.isValid?.() || !end.isValid?.()) return total
      const diff = end.diff(start, "minute")
      return diff > 0 ? total + diff : total
    }, 0)
  }

  const totalAvailableMinutes = getTotalAvailableMinutes(availableHours)
  // Get all available time slots
  const getAvailableTimeSlots = (availableHours) => {
    if (!Array.isArray(availableHours) || availableHours.length === 0) return []
    return availableHours
      .map((b) => {
        if (!b?.start || !b?.end) return null
        const start = typeof b.start === "string" && window.dayjs ? window.dayjs(b.start) : b.start
        const end = typeof b.end === "string" && window.dayjs ? window.dayjs(b.end) : b.end
        if (!start || !end || !start.isValid?.() || !end.isValid?.()) return null
        const diff = end.diff(start, "minute")
        return diff > 0 ? diff : null
      })
      .filter(Boolean)
  }

  // Check if any time slot can accommodate the requested duration
  const canAccommodateDuration = (duration, timeSlots) => {
    return timeSlots.some((slot) => slot >= duration)
  }

  const availableTimeSlots = getAvailableTimeSlots(availableHours)

  // Sync custom hour/minute inputs when value changes from outside (e.g. form.setFieldsValue on edit)
  useEffect(() => {
    if (value == null) {
      setCustomHour(0)
      setCustomMinute(0)
    } else {
      const isPredefined = predefinedOptions.some((opt) => opt.value === value)
      if (!isPredefined && !Number.isNaN(value)) {
        setCustomHour(Math.floor(value / 60))
        setCustomMinute(value % 60)
      }
    }
  }, [value])
  useEffect(() => {
    const total = (Number(customHour) || 0) * 60 + (Number(customMinute) || 0)
    if (error && total <= totalAvailableMinutes) {
      setError("")
    }
  }, [customHour, customMinute])

  const getDropdownRender = (menu) => (
    <CustomTimeDropdown
      menu={menu}
      customHour={customHour}
      customMinute={customMinute}
      error={error}
      setCustomHour={setCustomHour}
      setCustomMinute={setCustomMinute}
      handleCustomChange={handleCustomChange}
      label={t("global.enter_custom_duration")}
      availableTimeSlots={availableTimeSlots}
    />
  )

  const handleSelect = (val) => {
    onChange?.(val)
    setOpen(false)
  }

  const handleCustomChange = () => {
    const minuteValue = Number(customMinute) || 0

    if (minuteValue < 0 || minuteValue > 59) {
      setError(t("experiences.scheduling.errors.minute_limit"))
      return
    }
    const total = customHour * 60 + minuteValue

    if (availableTimeSlots.length > 0 && !canAccommodateDuration(total, availableTimeSlots)) {
      const maxSlot = Math.max(...availableTimeSlots)
      const hours = Math.floor(maxSlot / 60)
      const minutes = maxSlot % 60
      const timeString = [hours > 0 ? `${hours} hr` : "", minutes > 0 ? `${minutes} min` : ""].filter(Boolean).join(" ")
      setError(t(`Duration cannot exceed the maximum available time slot of ${timeString}`))
      return
    }

    onChange?.(total)
    setError("")
    setOpen(false)
  }

  return (
    <div>
      <Select
        open={open}
        onDropdownVisibleChange={(visible) => setOpen(visible)}
        style={{ width: "100%" }}
        placeholder="Select"
        value={
          predefinedOptions.find((opt) => opt.value === value)?.label ||
          (value ? `${Math.floor(value / 60)} hr ${value % 60} min` : undefined)
        }
        onSelect={handleSelect}
        dropdownRender={getDropdownRender}
      >
        {predefinedOptions.map((opt) => {
          const isTaken = takenDurations.has(opt.value)
          const isSlotDisabled = availableTimeSlots.length > 0 && !canAccommodateDuration(opt.value, availableTimeSlots)
          return (
            <Option key={opt.value} value={opt.value} disabled={isTaken || isSlotDisabled}>
              {isTaken ? `${opt.label} (${t("experiences.pricing.private.already_taken")})` : opt.label}
            </Option>
          )
        })}
      </Select>
    </div>
  )
}

ExperienceDuration.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  expandedRowData: PropTypes.object,
  value: PropTypes.number,
  onChange: PropTypes.func,
}
